import * as THREE from 'three';
import { AU } from '../config';
import type { Universe } from '../astro/Universe';
import { keplerPosition, daysSinceJ2000 } from '../astro/ephemeris';
import { formatDistance } from './format';

export type MapPoint = { key: string; label: string; position: THREE.Vector3; color: string; kind: 'player' | 'self' };

/**
 * Top-down ecliptic map with a logarithmic radius so Mercury and Pluto both fit.
 * Click a planet or player to select it as target.
 */
export class SystemMap {
  readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly orbitCache = new Map<string, THREE.Vector3[]>();
  private hits: { key: string; x: number; y: number }[] = [];
  open = false;
  onSelect: (key: string) => void = () => {};

  constructor(parent: HTMLElement, private readonly universe: Universe) {
    this.root = document.createElement('div');
    this.root.className = 'system-map hidden';
    this.root.innerHTML = `<div class="map-title">SOLAR SYSTEM <span>log scale · click a destination · M to close</span></div>`;
    this.canvas = document.createElement('canvas');
    this.root.appendChild(this.canvas);
    parent.appendChild(this.root);
    this.ctx = this.canvas.getContext('2d')!;
    this.canvas.addEventListener('click', (e) => {
      const r = this.canvas.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      let best: string | null = null, bd = 22;
      for (const h of this.hits) {
        const d = Math.hypot(h.x - x, h.y - y);
        if (d < bd) { bd = d; best = h.key; }
      }
      if (best) this.onSelect(best);
    });
  }

  toggle(): void {
    this.open = !this.open;
    this.root.classList.toggle('hidden', !this.open);
    if (this.open) document.exitPointerLock?.();
  }

  private project(p: THREE.Vector3, cx: number, cy: number, scale: number): [number, number] {
    const r = Math.hypot(p.x, p.y);
    const rr = Math.log10(1 + r / (0.08 * AU)) * scale;
    const a = Math.atan2(p.y, p.x);
    return [cx + Math.cos(a) * rr, cy - Math.sin(a) * rr];
  }

  private orbit(id: string): THREE.Vector3[] {
    let pts = this.orbitCache.get(id);
    if (pts) return pts;
    const def = this.universe.get(id).def;
    pts = [];
    const T = daysSinceJ2000(this.universe.timeMs) / 36525;
    const el = def.kepler!;
    const periodCy = 360 / el.L[1];
    for (let i = 0; i <= 180; i++) {
      pts.push(keplerPosition(el, T + (periodCy * i) / 180, new THREE.Vector3()));
    }
    this.orbitCache.set(id, pts);
    return pts;
  }

  draw(points: MapPoint[], targetKey: string | null): void {
    if (!this.open) return;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    if (this.canvas.width !== w * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
    }
    const g = this.ctx;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2;
    const scale = (Math.min(w, h) * 0.47) / Math.log10(1 + (50 * AU) / (0.08 * AU));
    this.hits = [];

    // AU rings
    g.strokeStyle = 'rgba(127,231,255,0.07)';
    g.fillStyle = 'rgba(127,231,255,0.35)';
    g.font = '10px Chakra Petch, sans-serif';
    for (const au of [1, 5, 10, 30]) {
      const r = Math.log10(1 + au / 0.08) * scale;
      g.beginPath();
      g.arc(cx, cy, r, 0, Math.PI * 2);
      g.stroke();
      g.fillText(`${au} AU`, cx + r + 4, cy - 4);
    }

    // Asteroid belt band
    const r1 = Math.log10(1 + 2.1 / 0.08) * scale, r2 = Math.log10(1 + 3.3 / 0.08) * scale;
    g.fillStyle = 'rgba(200,180,150,0.06)';
    g.beginPath();
    g.arc(cx, cy, r2, 0, Math.PI * 2);
    g.arc(cx, cy, r1, 0, Math.PI * 2, true);
    g.fill();

    for (const b of this.universe.bodies) {
      const def = b.def;
      if (def.kind === 'moon') continue;
      if (def.kepler) {
        g.strokeStyle = def.id === 'earth' ? 'rgba(79,143,255,0.35)' : 'rgba(255,255,255,0.12)';
        g.beginPath();
        this.orbit(def.id).forEach((p, i) => {
          const [x, y] = this.project(p, cx, cy, scale);
          if (i === 0) g.moveTo(x, y);
          else g.lineTo(x, y);
        });
        g.stroke();
      }
      const [x, y] = def.id === 'sun' ? [cx, cy] : this.project(b.position, cx, cy, scale);
      const key = `body:${def.id}`;
      const size = def.id === 'sun' ? 8 : def.kind === 'dwarf' ? 3 : 5;
      g.fillStyle = def.mapColor;
      g.beginPath();
      g.arc(x, y, size, 0, Math.PI * 2);
      g.fill();
      if (key === targetKey) {
        g.strokeStyle = '#ffd27a';
        g.lineWidth = 1.5;
        g.strokeRect(x - size - 5, y - size - 5, (size + 5) * 2, (size + 5) * 2);
        g.lineWidth = 1;
      }
      g.fillStyle = 'rgba(255,255,255,0.8)';
      g.font = '12px Chakra Petch, sans-serif';
      g.fillText(def.name, x + size + 4, y + 4);
      this.hits.push({ key, x, y });
    }

    for (const p of points) {
      const [x, y] = this.project(p.position, cx, cy, scale);
      g.fillStyle = p.kind === 'self' ? '#7fe7ff' : p.color;
      g.beginPath();
      g.moveTo(x, y - 6);
      g.lineTo(x + 5, y + 4);
      g.lineTo(x - 5, y + 4);
      g.closePath();
      g.fill();
      g.fillStyle = p.kind === 'self' ? '#7fe7ff' : 'rgba(255,255,255,0.85)';
      g.fillText(p.kind === 'self' ? 'YOU' : p.label, x + 8, y - 6);
      if (p.kind === 'player') this.hits.push({ key: p.key, x, y });
    }

    const self = points.find((p) => p.kind === 'self');
    if (self) {
      const earth = this.universe.get('earth');
      g.fillStyle = 'rgba(127,231,255,0.7)';
      g.fillText(`Distance to Sun ${formatDistance(self.position.length())} · to Earth ${formatDistance(self.position.distanceTo(earth.position))}`, 16, h - 16);
    }
  }
}
